import type {Operation} from '@apollo/client';

import {getInteropAccountId} from '../../../interopAccountId';
import {ACCOUNT_HEADER} from '../../../addContextHeaders';
import type {PathBuilderParams, RestLinkOperationContext} from './types';

export type InteropFields = {
  query: Record<string, boolean>;
  mutation: Record<string, boolean>;
  subscription: Record<string, boolean>;
};

// Rewrites variables, headers, and per-field input for interop operations
// running in an org context. Called from buildRestPath once per top-level
// field.
export default function rewriteInteropParams(
  operation: Operation,
  params: PathBuilderParams,
  interopFields: InteropFields | undefined,
  observabilityFns?: RestLinkOperationContext['observabilityFns'],
) {
  if (!interopFields) {
    return;
  }

  const fieldName = params.fieldName;
  const opType = params.context.mainDefinition.operation;

  if (!interopFields[opType]?.[fieldName]) {
    return;
  }

  const interopAccountId = getInteropAccountId();

  if (!interopAccountId) {
    return;
  }

  if (params.args?.input?.root_compartment_id) {
    const v1Context = operation.variables.v1Context as
      | {id?: string}
      | undefined;

    if (v1Context?.id && v1Context.id !== interopAccountId) {
      observabilityFns?.errors?.warning(
        'v1Context.id does not match interopAccountId when root_compartment_id is present [v1]',
        {
          project: 'sail_core',
          extras: {
            v1ContextId: v1Context.id,
            interopAccountId,
            fieldName: params.fieldName,
          },
        },
      );
    } else if (Math.random() < 0.0001) {
      observabilityFns?.analytics?.track('interop_root_compartment_id_match', {
        project: 'sail_core',
        v1_context_id: v1Context?.id,
        interop_account_id: interopAccountId,
        field_name: params.fieldName,
      });
    }
  }

  const context = operation.getContext();
  let orgId: string | undefined = context.__interopOrgId;

  // Per-operation rewrite: only run once per operation. After the first
  // interop field rewrites v1Context.id, subsequent fields see the stored
  // __interopOrgId in context instead of repeating the rewrite.
  if (!orgId) {
    const v1Context = operation.variables.v1Context as
      | {id?: string}
      | undefined;

    if (!v1Context?.id?.startsWith('org_')) {
      return;
    }

    orgId = v1Context.id;

    operation.variables = {
      ...operation.variables,
      v1Context: {...v1Context, id: interopAccountId},
    };

    // Rewrite the Stripe-Account header if it currently matches the org ID
    if (context.headers?.[ACCOUNT_HEADER] === orgId) {
      operation.setContext({
        headers: {
          ...context.headers,
          [ACCOUNT_HEADER]: interopAccountId,
        },
        __interopOrgId: orgId,
      });
    } else {
      operation.setContext({
        __interopOrgId: orgId,
      });
    }

    // Add root_compartment_id to the input.
    if (params.args) {
      params.args = {
        ...params.args,
        input: {
          ...(params.args.input || {}),
          root_compartment_id: orgId,
        },
      };
    }
  }
}
